import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/auth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // 'login' | 'newPassword' (invited staff, NEW_PASSWORD_REQUIRED) |
  // 'forgot' (request reset code) | 'reset' (code + new password)
  const [mode, setMode] = useState<'login' | 'newPassword' | 'forgot' | 'reset'>('login');
  const [challengeSession, setChallengeSession] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate("/dashboard");
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result?.challengeName === "NEW_PASSWORD_REQUIRED" && result.session) {
        setChallengeSession(result.session);
        setMode("newPassword");
        return;
      }
      if (result?.challengeName) {
        setError(`Unsupported sign-in challenge: ${result.challengeName}`);
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to login. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };


  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await authService.completeNewPassword(email, newPassword, challengeSession);
      if (result.accessToken) {
        navigate("/dashboard");
      } else {
        setError("Unexpected response — try signing in again");
        setMode("login");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Could not set the new password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await authService.forgotPassword(email);
      setNotice("If that account exists, a reset code was emailed to it.");
      setMode("reset");
    } catch (err: any) {
      setError(err.response?.data?.error || "Could not start the reset");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await authService.confirmForgotPassword(email, resetCode, newPassword);
      setNotice("Password updated — sign in with it now.");
      setMode("login");
      setPassword("");
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err: any) {
      setError(err.response?.data?.error || "Could not reset the password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center mb-2">
              <img
                src="/voyaxhealth-cropped.png"
                alt="Voyax Health Logo"
                className="h-20 w-20 object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Voyax Health
            </h1>
            <p className="text-teal-600 font-semibold text-sm">
              Pharmacy Portal
            </p>
          </div>

          {/* Form */}
          {mode === "login" && (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="pharmacist@voyaxhealth.com"
                autoComplete="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400"
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 text-gray-400 hover:text-gray-600 focus:outline-none w-6 h-6 flex items-center justify-center"
                  style={{ transform: "translateY(-50%)" }}
                >
                  {showPassword ? (
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-error-bg border border-error-border rounded-lg text-error-text text-sm">
                <span className="text-base">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing In...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
              <button
                type="button"
                onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                className="mt-4 w-full text-center text-sm text-teal-700 hover:text-teal-900 underline"
              >
                Forgot password?
              </button>
            </>
          )}
          {mode === "newPassword" && (
            <form onSubmit={handleNewPassword} className="space-y-5">
              <p className="text-sm text-gray-600">
                Welcome! Choose a new password to replace your temporary one.
              </p>
              <input type="password" required placeholder="New password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="New password" />
              <input type="password" required placeholder="Confirm new password" value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="Confirm new password" />
              <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 transition-all duration-200">
                {loading ? "Saving…" : "Set password and sign in"}
              </button>
            </form>
          )}
          {mode === "forgot" && (
            <form onSubmit={handleForgot} className="space-y-5">
              <p className="text-sm text-gray-600">Enter your work email and we&apos;ll send a reset code.</p>
              <input type="email" required placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="Email" />
              <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 transition-all duration-200">
                {loading ? "Sending…" : "Send reset code"}
              </button>
              <button type="button" onClick={() => { setMode("login"); setError(""); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline">
                Back to sign in
              </button>
            </form>
          )}
          {mode === "reset" && (
            <form onSubmit={handleReset} className="space-y-5">
              <p className="text-sm text-gray-600">Enter the code from your email and your new password.</p>
              <input type="text" required placeholder="Reset code" value={resetCode}
                onChange={(e) => setResetCode(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="Reset code" />
              <input type="password" required placeholder="New password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="New password" />
              <input type="password" required placeholder="Confirm new password" value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 placeholder-gray-400" aria-label="Confirm new password" />
              <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 transition-all duration-200">
                {loading ? "Updating…" : "Set new password"}
              </button>
              <button type="button" onClick={() => { setMode("login"); setError(""); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline">
                Back to sign in
              </button>
            </form>
          )}
          {notice && <p className="mt-3 text-sm text-teal-700">{notice}</p>}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              🔒 Secure access for authorized pharmacy personnel
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
